import {
  canUseWhisperWeb,
  downloadWhisperModel,
  getAvailableModels,
  resampleTo16Khz,
  toCaptions,
  transcribe,
  type WhisperWebLanguage,
  type WhisperWebModel,
} from '@remotion/whisper-web';
import { parseMedia } from '@remotion/media-parser';
import type { Caption } from '@remotion/captions';
import { transcribeOnWebGpu, type WebGpuModelId } from './asr/webgpu';
import type { ModelDownload } from './asr/downloadProgress';
import { shiftCaptions } from './captions';

export type Stage =
  | 'idle'
  | 'probing'
  | 'extracting-audio'
  | 'downloading-model'
  | 'transcribing'
  | 'ready'
  | 'error';

export const STAGE_LABEL: Record<Stage, string> = {
  idle: 'Waiting for a video',
  probing: 'Reading video metadata',
  'extracting-audio': 'Extracting and resampling audio to 16 kHz',
  'downloading-model': 'Downloading speech model',
  transcribing: 'Transcribing',
  ready: 'Ready',
  error: 'Failed',
};

export type VideoMeta = {
  width: number;
  height: number;
  fps: number;
  durationInSeconds: number;
  /** True when the container reported no frame rate and 30 was assumed. */
  fpsAssumed: boolean;
};

export type ModelInfo = { name: WhisperWebModel; downloadSize: number };

// Synchronous despite the name — it reads a static size table, not the network.
export const listModels = (): ModelInfo[] => getAvailableModels();

export const checkSupport = (model: WhisperWebModel) => canUseWhisperWeb(model);

/**
 * Some containers (notably variable-frame-rate recordings and certain WebM
 * muxers) report no duration or frame rate. A `<video>` element will still
 * give a duration for most of them, so ask it before giving up.
 */
const durationFromElement = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null);
    video.onerror = () => finish(null);
    // Never hang the upload on a file the element also cannot read.
    setTimeout(() => finish(null), 10_000);
    video.src = url;
  });

export const probeVideo = async (file: File): Promise<VideoMeta> => {
  const parsed = await parseMedia({
    src: file,
    fields: {
      dimensions: true,
      fps: true,
      durationInSeconds: true,
      numberOfAudioChannels: true,
    },
    acknowledgeRemotionLicense: true,
  });

  if (!parsed.dimensions) {
    throw new Error('No video track found in this file.');
  }

  // Without this the failure surfaces much later, deep inside resampleTo16Khz,
  // as something unrelated to the actual problem.
  if (parsed.numberOfAudioChannels === 0) {
    throw new Error(
      'This video has no audio track, so there is nothing to transcribe.',
    );
  }

  const durationInSeconds = parsed.durationInSeconds ?? (await durationFromElement(file));

  // Falling back to 0 here used to be silent, and every downstream frame count
  // is `duration * fps` — so a missing duration produced a one-frame export
  // with no warning. Refusing is far better than shipping that.
  if (durationInSeconds === null || durationInSeconds <= 0) {
    throw new Error(
      'Could not read this video\'s duration, so it cannot be captioned or exported. ' +
        'Re-encoding it (for example with ffmpeg) usually fixes the metadata.',
    );
  }

  return {
    width: parsed.dimensions.width,
    height: parsed.dimensions.height,
    // A wrong frame rate does not desync captions — the composition derives both
    // the video time and the caption time from the same value — but it does set
    // the export's output rate and the trimmer's snap grid, so it is surfaced.
    fps: parsed.fps ?? 30,
    fpsAssumed: parsed.fps === null || parsed.fps === undefined,
    durationInSeconds,
  };
};

export type PipelineCallbacks = {
  onStage: (stage: Stage) => void;
  /** 0..1 within the current stage. */
  onProgress: (progress: number) => void;
  /** Bytes behind the 'downloading-model' stage, so it can read as a size. */
  onDownload?: (download: ModelDownload) => void;
  /** Fires as transcription streams in, so the UI can show text early. */
  onPartial?: (captions: Caption[]) => void;
};

export type EngineId = 'cpu' | 'gpu';

export const ENGINES: { id: EngineId; name: string; hint: string }[] = [
  {
    id: 'gpu',
    name: 'WebGPU (transformers.js)',
    hint: 'ONNX Runtime Web on the GPU — several times faster',
  },
  {
    id: 'cpu',
    name: 'WASM (@remotion/whisper-web)',
    hint: 'whisper.cpp, 4 CPU threads — no GPU option exists',
  },
];

type TimeRange = { startSec: number; endSec: number };

export type TranscribeOptions = {
  engine: EngineId;
  cpuModel: WhisperWebModel;
  gpuModel: WebGpuModelId;
  language: WhisperWebLanguage;
  /** Transcribe only this slice of the timeline. Null means the whole clip. */
  range: TimeRange | null;
};

const SAMPLE_RATE = 16_000;

export const transcribeFile = async (
  file: File,
  opts: TranscribeOptions,
  cb: PipelineCallbacks,
): Promise<Caption[]> => {
  const { language } = opts;

  // Audio extraction is shared: both engines want a mono 16 kHz Float32Array,
  // so this step is identical regardless of where inference runs.
  cb.onStage('extracting-audio');
  cb.onProgress(0);
  const fullWaveform = await resampleTo16Khz({
    file,
    onProgress: cb.onProgress,
  });

  // Slicing the waveform is the whole trick: transcribing 20s out of a 10min
  // video costs 20s of inference, not 10min.
  let channelWaveform = fullWaveform;
  let offsetMs = 0;

  if (opts.range) {
    const from = Math.max(0, Math.floor(opts.range.startSec * SAMPLE_RATE));
    const to = Math.min(fullWaveform.length, Math.ceil(opts.range.endSec * SAMPLE_RATE));
    if (to - from < SAMPLE_RATE / 2) {
      throw new Error('Selected range is under half a second of audio.');
    }
    // .slice() rather than .subarray(): a subarray keeps a non-zero byteOffset
    // into the original buffer, and both inference backends want a plain
    // Float32Array that starts at 0.
    channelWaveform = fullWaveform.slice(from, to);
    offsetMs = (from / SAMPLE_RATE) * 1000;
  }

  if (opts.engine === 'gpu') {
    cb.onStage('downloading-model');
    cb.onProgress(0);
    const captions = await transcribeOnWebGpu({
      waveform: channelWaveform,
      model: opts.gpuModel,
      language,
      onModelProgress: (download) => {
        cb.onProgress(download.progress);
        cb.onDownload?.(download);
      },
      onLoaded: () => {
        cb.onStage('transcribing');
        cb.onProgress(0);
      },
      onProgress: cb.onProgress,
    });
    cb.onStage('ready');
    return shiftCaptions(captions, offsetMs);
  }

  const model = opts.cpuModel;
  cb.onStage('downloading-model');
  cb.onProgress(0);
  await downloadWhisperModel({
    model,
    // One file here rather than the GPU engine's dozen, so its own progress is
    // already monotonic — it just needs the same shape to render.
    onProgress: ({ progress, downloadedBytes, totalBytes }) => {
      cb.onProgress(progress);
      cb.onDownload?.({
        progress,
        loadedBytes: downloadedBytes,
        totalBytes,
        determinate: totalBytes > 0,
      });
    },
  });

  cb.onStage('transcribing');
  cb.onProgress(0);
  const result = await transcribe({
    channelWaveform,
    model,
    language,
    onProgress: cb.onProgress,
    onTranscriptionChunk: (chunk) => {
      if (!cb.onPartial) return;
      cb.onPartial(shiftCaptions(toCaptions({ whisperWebOutput: chunk }).captions, offsetMs));
    },
  });

  cb.onStage('ready');
  return shiftCaptions(toCaptions({ whisperWebOutput: result }).captions, offsetMs);
};

/**
 * Whisper's WASM build is exercised far more on Chromium than elsewhere.
 * "attempting to construct out-of-bounds Float32Array on ArrayBuffer" is
 * SpiderMonkey's JSMSG_TYPED_ARRAY_CONSTRUCT_BOUNDS — i.e. Firefox — so
 * knowing the engine is half the diagnosis.
 */
export const detectEngine = (): 'chromium' | 'safari' | 'firefox' | 'unknown' => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/Edg\/|Chrome\/|Chromium\//.test(ua)) return 'chromium';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Safari\//.test(ua)) return 'safari';
  return 'unknown';
};

/**
 * Whisper here runs on the CPU (whisper.cpp → WASM SIMD, 4 threads) — there is
 * no GPU option in `transcribe()`. This probe reports whether a GPU path would
 * even be available if the ASR engine were swapped for a WebGPU one.
 * `navigator.gpu` existing is not enough; an adapter must actually be grantable.
 */
export const probeWebGPU = async (): Promise<'yes' | 'no-adapter' | 'unsupported'> => {
  if (typeof navigator === 'undefined' || !('gpu' in navigator)) return 'unsupported';
  try {
    const adapter = await (navigator as Navigator & {
      gpu: { requestAdapter: () => Promise<unknown | null> };
    }).gpu.requestAdapter();
    return adapter ? 'yes' : 'no-adapter';
  } catch {
    return 'no-adapter';
  }
};

export const describeEnvironment = (): string =>
  [
    `engine=${detectEngine()}`,
    `isolated=${typeof window !== 'undefined' && window.crossOriginIsolated}`,
    `sab=${typeof SharedArrayBuffer !== 'undefined'}`,
    `webgpu=${typeof navigator !== 'undefined' && 'gpu' in navigator}`,
    `hw=${typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : '?'}`,
  ].join('  ');

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
};

export const formatTimecode = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  const cs = Math.floor((ms % 1000) / 10);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
};
